/**
 * Pure core for the Home "Today's worklist" (KAN-2553).
 *
 * Everything in this file is a pure function: no fetching, no clock reads, no
 * navigation. The hook and the components are the imperative shell. This is
 * what makes the filter set, the ordering and the running row index testable
 * without mocking react-query.
 */

import { ITableFilter, TableFilterType } from "../../../models/components/table/table-filter.model";
import { AlertReason } from "../../../models/entities/alert.model";
import { Task } from "../../../models/entities/task.model";
import { alertSeverityRank } from "../../../utils/alert-priority";

/**
 * Sections of the worklist, in the order they render on the page. The order is
 * declared once, here, because the row index runs *across* sections — the
 * KAN-2555 mockup numbers reminders 8–13, continuing from the critical alerts
 * above them. A new section adds its id to this list, in render order.
 *
 * KAN-2641: SOOR (Sustained out-of-range) alerts are split into two sections,
 * High and Low, each with its own collapsible header and count badge.
 * Maria 2026-10-16: SOOR High/Low sit directly under Critical alerts, above Consent needed and Reminders.
 */
export const WORKLIST_SECTION_IDS = [
	"critical-alerts",
	"soor-alerts-high",
	"soor-alerts-low",
	"consent",
	"reminders",
] as const;
export type WorklistSectionId = (typeof WORKLIST_SECTION_IDS)[number];

/**
 * The filter set for "my alerts" with a given reason.
 *
 * - `category=alert` + `resolved=false` — the same always-on pair the Requires
 *   attention table sends.
 * - `reason=<reason>` — filters to the given alert reason. For critical reading,
 *   Maria's answer (2026-09-14): "critical" is the *Critical reading value*
 *   category, NOT `severity=CRITICAL`. Severity is a transient rung on an
 *   escalation ladder that most critical readings never reach and that
 *   abnormal-weight-gain alerts never reach at all, so a severity filter would
 *   hide most of the list. Each row instead renders its own severity as its
 *   priority badge, which is only meaningful *because* the list is filtered by
 *   reason.
 * - `assignee=<me>` — applied UNCONDITIONALLY, including in admin view. The
 *   Requires attention table drops this filter for admins; the worklist must
 *   not (Maria, KAN-2552 Q3: "a focused list for what someone needs to
 *   complete"). Do not copy the `if (!isAdminViewMode())` guard from
 *   ParticipantsAlerts.tsx.
 */
export const buildMyAlertsFilters = (assigneeId: number, reason: AlertReason): ITableFilter[] => [
	{
		field: "category",
		type: TableFilterType.SELECT_MULTIPLE,
		value: ["alert"],
	},
	{
		field: "resolved",
		type: TableFilterType.SELECT_MULTIPLE,
		value: ["false"],
	},
	{
		field: "reason",
		type: TableFilterType.SELECT_MULTIPLE,
		value: [reason],
	},
	{
		field: "assignee",
		type: TableFilterType.SELECT_MULTIPLE,
		value: [`${assigneeId}`],
	},
];

/** Filters to CRITICAL_READING alerts. See buildMyAlertsFilters for the rationale on critical reading vs severity. */
export const buildMyCriticalAlertsFilters = (assigneeId: number): ITableFilter[] =>
	buildMyAlertsFilters(assigneeId, AlertReason.CRITICAL_READING);

/** The timestamp an alert is ordered by, as epoch ms. Unknown dates sort last. */
export const alertTimestamp = (alert: Task): number => {
	const raw = alert.taskDate ?? alert.createdOn;
	if (!raw) return Number.NEGATIVE_INFINITY;
	const parsed = new Date(raw).getTime();
	return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
};

/**
 * Priority first, then newest (Maria's answer #6).
 *
 * Deliberately explicit about both keys rather than relying on the server
 * having returned the page newest-first and a stable sort preserving it: the
 * worklist is not paginated by the user and must be correct on its own terms.
 * Returns a new array; the input is not mutated.
 */
export const sortCriticalAlerts = <T extends Task>(alerts: T[]): T[] =>
	[...alerts].sort((a, b) => {
		const byPriority = alertSeverityRank(a.severity) - alertSeverityRank(b.severity);
		if (byPriority !== 0) return byPriority;
		return alertTimestamp(b) - alertTimestamp(a);
	});

export interface GroupedSoorAlerts<T extends Task = Task> {
	high: T[];
	low: T[];
}

/** severities that put a SOOR alert in the High group. SOOR only emits LOW/HIGH today; CRITICAL/MEDIUM are defensive. */
const SOOR_HIGH_SEVERITIES = new Set(["HIGH", "CRITICAL", "MEDIUM"]);

/**
 * Compare two tasks by `alertTimestamp`, placing oldest first (ascending).
 * Unknown dates (alertTimestamp === -Infinity) are placed LAST within a group.
 */
export const compareOldestFirst = (a: Task, b: Task): number => {
	const aTime = alertTimestamp(a);
	const bTime = alertTimestamp(b);

	// Both are -Infinity: tie
	if (aTime === Number.NEGATIVE_INFINITY && bTime === Number.NEGATIVE_INFINITY) {
		return 0;
	}
	// Only a is -Infinity: a goes last
	if (aTime === Number.NEGATIVE_INFINITY) {
		return 1;
	}
	// Only b is -Infinity: b goes last
	if (bTime === Number.NEGATIVE_INFINITY) {
		return -1;
	}
	// Both have timestamps: sort ascending (oldest first)
	return aTime - bTime;
};

/**
 * Group SOOR alerts by severity and sort by age within each group.
 *
 * KAN-2641, Maria 2026-10-15: High/Low is the alert's existing severity
 * (LOW→HIGH escalation), so an escalated alert moves to High; oldest first
 * within a group (open longest at top) — stated assumption to Maria.
 *
 * Severity in SOOR_HIGH_SEVERITIES → high; everything else including
 * missing/null severity → low. Each group sorted OLDEST FIRST by `alertTimestamp`
 * (ascending), with unknown dates (alertTimestamp === -Infinity) placed LAST.
 * Must not mutate input; return new arrays.
 */
export const groupSoorAlerts = <T extends Task>(alerts: T[]): GroupedSoorAlerts<T> => {
	const high: T[] = [];
	const low: T[] = [];

	for (const alert of alerts) {
		if (SOOR_HIGH_SEVERITIES.has(alert.severity as string)) {
			high.push(alert);
		} else {
			low.push(alert);
		}
	}

	high.sort(compareOldestFirst);
	low.sort(compareOldestFirst);

	return { high, low };
};

/**
 * Consent needed (KAN-2647, Maria 2026-10-15): one flat list, HIGH before LOW
 * (by `alertSeverityRank`), then OLDEST first within a priority (open longest
 * at top), unknown dates last. Returns a new array; the input is not mutated.
 */
export const sortConsentTasks = <T extends Task>(tasks: T[]): T[] =>
	[...tasks].sort((a, b) => {
		const byPriority = alertSeverityRank(a.severity) - alertSeverityRank(b.severity);
		if (byPriority !== 0) return byPriority;
		return compareOldestFirst(a, b);
	});

/**
 * The participant phone number for the shared call control.
 *
 * Every `/api/tasks/` row carries a nested participant snapshot (`userState`),
 * so this is a field read and never an extra request per row. A missing number
 * yields null — the row then hides the Call control rather than dialling
 * something wrong.
 *
 * The number lives in `userState.phoneNumber` (user_state_tmp.phone_number, the
 * same field the Participants table dials). The snapshot's `phone` column is
 * empty for every participant in prod and UAT, so reading only `phone` hid Call
 * on every Home alert row (KAN-2641). `phone` and `phoneNumberPretty` are kept
 * as fallbacks only.
 */
export const getAlertParticipantPhone = (alert: Task): string | null => {
	const userState = alert.userState as
		| {
				phoneNumber?: string | null;
				phone?: string | null;
				countryCode?: string | null;
				phoneNumberPretty?: string | null;
		  }
		| undefined
		| null;
	const phone = userState?.phoneNumber?.trim() || userState?.phone?.trim();
	if (phone) {
		const countryCode = userState?.countryCode?.trim();
		return countryCode && !phone.startsWith(countryCode) ? `${countryCode}${phone}` : phone;
	}
	const pretty = userState?.phoneNumberPretty?.trim();
	return pretty ? pretty : null;
};

export interface WorklistParticipant {
	id: number | null;
	kannactId: string | null;
	name: string;
}

/** Participant identity for a row, read from the task's nested snapshot. */
export const getAlertParticipant = (alert: Task): WorklistParticipant => {
	const userState = alert.userState as
		| { id?: number; kannactId?: string; firstName?: string; lastName?: string }
		| undefined
		| null;
	return {
		id: alert.participantId ?? userState?.id ?? null,
		kannactId: userState?.kannactId ?? null,
		name: [userState?.firstName, userState?.lastName]
			.map((part) => part?.trim())
			.filter(Boolean)
			.join(" "),
	};
};

/**
 * Participant status (ACTIVE / PROSPECTIVE / ...) from the task's nested snapshot;
 * null when absent.
 */
export const getAlertParticipantStatus = (alert: Task): string | null => {
	const status = (alert.userState as { status?: string } | undefined | null)?.status?.trim();
	return status || null;
};

/**
 * Where row 1 of a section sits in the page-wide numbering.
 *
 * Sections publish their counts to the page-level worklist context; this sums
 * the counts of every section declared *before* the given one. With a single
 * section today the answer is always 0 — the point is that KAN-2555's
 * reminders start at 5 when four critical alerts sit above them, without either
 * section knowing about the other.
 */
export const computeSectionRowOffset = (
	sectionId: string,
	sectionOrder: readonly string[],
	counts: Record<string, number>
): number =>
	sectionOrder
		.slice(0, Math.max(sectionOrder.indexOf(sectionId), 0))
		.reduce((total, id) => total + (counts[id] ?? 0), 0);

/**
 * The optimistic-resolve arithmetic now lives with the rest of the shared
 * resolve logic (Home + both Requires attention tables) in
 * `utils/resolved-alert-cache.ts`; re-exported here for existing callers.
 */
export { removeAlertFromCache } from "../../../utils/resolved-alert-cache";
